import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import { api } from '../services/api';

interface Transaction {
  id: string;
  date: string;
  bookingDate: string;
  amountEur: number;
  type: string;
  rawPayeeName: string;
  selitys: string;
  viesti: string;
  archiveId: string;
  accountId: string;
  suggestedCategoryId?: string;
  cardId?: string;
}

interface Allocation {
  id: string;
  transactionId: string | null;
  amountEur: number;
  categoryId: string;
  note?: string;
}

interface Category {
  id: string;
  name: string;
  parentId?: string;
  type: string;
}

interface SplitLine {
  amountEur: number;
  categoryId: string;
  note: string;
}

export default function TransactionsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [month, setMonth] = useState(new Date().toISOString().substring(0, 7));
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitTx, setSplitTx] = useState<Transaction | null>(null);
  const [splitLines, setSplitLines] = useState<SplitLine[]>([]);
  const [splitError, setSplitError] = useState<string | null>(null);
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  const formatEur = (amount: number) =>
    new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(amount);

  useEffect(() => {
    api.get<Category[]>('/api/categories').then(setCategories);
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get<Transaction[]>(`/api/transactions?month=${month}`).catch(() => [] as Transaction[]),
      api.get<Allocation[]>(`/api/allocations?month=${month}`).catch(() => [] as Allocation[]),
    ])
      .then(([txs, allocs]) => {
        setTransactions(txs);
        setAllocations(allocs);
      })
      .finally(() => setLoading(false));
  }, [month]);

  // Build allocation lookup: transactionId → allocations
  const allocsByTx = React.useMemo(() => {
    const map = new Map<string, Allocation[]>();
    for (const a of allocations) {
      if (!a.transactionId) continue;
      const arr = map.get(a.transactionId) || [];
      arr.push(a);
      map.set(a.transactionId, arr);
    }
    return map;
  }, [allocations]);

  // Category helpers
  const categoryLabel = React.useCallback(
    (catId: string) => {
      const cat = categories.find((c) => c.id === catId);
      if (!cat) return catId;
      if (cat.parentId) {
        const parent = categories.find((c) => c.id === cat.parentId);
        return parent ? `${parent.name} > ${cat.name}` : cat.name;
      }
      return cat.name;
    },
    [categories],
  );

  // Selectable categories (leaf nodes, no parent-only entries)
  const selectableCategories = React.useMemo(() => {
    const parentIds = new Set(categories.filter((c) => c.parentId).map((c) => c.parentId));
    return categories.filter((c) => !parentIds.has(c.id));
  }, [categories]);

  const handleCategoryChange = async (allocationId: string, categoryId: string) => {
    setSavingIds((prev) => new Set(prev).add(allocationId));
    try {
      const updated = await api.patch<Allocation>(`/api/allocations/${allocationId}`, { categoryId });
      setAllocations((prev) =>
        prev.map((a) => (a.id === allocationId ? { ...a, categoryId: updated.categoryId } : a)),
      );
      setSavedIds((prev) => new Set(prev).add(allocationId));
      setTimeout(() => setSavedIds((prev) => {
        const next = new Set(prev);
        next.delete(allocationId);
        return next;
      }), 1500);
    } catch {
      // Silently ignore — user can retry
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(allocationId);
        return next;
      });
    }
  };

  const openSplitDialog = (tx: Transaction) => {
    setSplitTx(tx);
    const txAllocs = allocsByTx.get(tx.id);
    if (txAllocs && txAllocs.length > 0) {
      setSplitLines(
        txAllocs.map((a) => ({ amountEur: a.amountEur, categoryId: a.categoryId, note: a.note || '' })),
      );
    } else {
      setSplitLines([
        { amountEur: tx.amountEur, categoryId: tx.suggestedCategoryId || '', note: '' },
      ]);
    }
    setSplitError(null);
    setSplitDialogOpen(true);
  };

  const addSplitLine = () => {
    setSplitLines([...splitLines, { amountEur: 0, categoryId: '', note: '' }]);
  };

  const removeSplitLine = (idx: number) => {
    setSplitLines(splitLines.filter((_, i) => i !== idx));
  };

  const updateSplitLine = (idx: number, field: keyof SplitLine, value: any) => {
    const updated = [...splitLines];
    (updated[idx] as any)[field] = value;
    setSplitLines(updated);
  };

  const handleSplitSave = async () => {
    if (!splitTx) return;
    setSplitError(null);

    const sum = splitLines.reduce((acc, l) => acc + Number(l.amountEur), 0);
    const diff = Math.abs(splitTx.amountEur - sum);
    if (diff > 0.01) {
      setSplitError(
        `Jaon summa (${sum.toFixed(2)}) ei vastaa tapahtumaa (${splitTx.amountEur.toFixed(2)})`,
      );
      return;
    }

    try {
      await api.post('/api/allocations/split', {
        transactionId: splitTx.id,
        allocations: splitLines.map((l) => ({
          amountEur: Number(l.amountEur),
          categoryId: l.categoryId,
          note: l.note || undefined,
        })),
      });
      // Reload allocations to reflect the split
      const updatedAllocs = await api.get<Allocation[]>(`/api/allocations?month=${month}`);
      setAllocations(updatedAllocs);
      setSplitDialogOpen(false);
    } catch (err: any) {
      setSplitError(err.message);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <Typography variant="h5" fontWeight={600} sx={{ flexGrow: 1 }}>
          Tapahtumat
        </Typography>
        <TextField
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
        />
      </Box>

      {loading ? (
        <Typography>Ladataan...</Typography>
      ) : transactions.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary">Ei tapahtumia kuukaudelle {month}</Typography>
          </CardContent>
        </Card>
      ) : (
        transactions.map((tx) => {
          const txAllocs = allocsByTx.get(tx.id) || [];
          const isSplit = txAllocs.length > 1;
          const primaryAlloc = txAllocs[0];
          const primaryCatId = primaryAlloc?.categoryId || 'cat-uncategorized';
          const isUncategorized = primaryCatId === 'cat-uncategorized';

          return (
            <Card
              key={tx.id}
              sx={{
                mb: 1.5,
                borderLeft: isUncategorized ? '4px solid' : undefined,
                borderLeftColor: isUncategorized ? 'warning.main' : undefined,
              }}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body1" fontWeight={600} noWrap>
                      {tx.rawPayeeName || tx.selitys || 'Tuntematon'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tx.date}
                      {tx.selitys ? ` · ${tx.selitys}` : ''}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      color={tx.amountEur >= 0 ? 'success.main' : 'error.main'}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {formatEur(tx.amountEur)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => openSplitDialog(tx)}
                      aria-label="jaa tapahtuma"
                    >
                      <CallSplitIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Category display / editor */}
                <Box mt={1} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  {isSplit ? (
                    // Split transaction: show chips for each allocation
                    txAllocs.map((a) => (
                      <Chip
                        key={a.id}
                        size="small"
                        label={`${categoryLabel(a.categoryId)} (${formatEur(a.amountEur)})`}
                        color={a.categoryId === 'cat-uncategorized' ? 'warning' : 'default'}
                        variant="outlined"
                      />
                    ))
                  ) : primaryAlloc ? (
                    // Single allocation: inline category selector
                    <>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                          value={primaryCatId}
                          onChange={(e) => handleCategoryChange(primaryAlloc.id, e.target.value)}
                          disabled={savingIds.has(primaryAlloc.id)}
                          sx={{
                            fontSize: '0.8125rem',
                            '& .MuiSelect-select': { py: 0.5 },
                            bgcolor: isUncategorized ? 'warning.50' : undefined,
                          }}
                        >
                          {selectableCategories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8125rem' }}>
                              {categoryLabel(cat.id)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {savingIds.has(primaryAlloc.id) && (
                        <CircularProgress size={18} />
                      )}
                      {savedIds.has(primaryAlloc.id) && (
                        <CheckIcon color="success" fontSize="small" />
                      )}
                    </>
                  ) : (
                    <Chip size="small" label="Ei kohdistusta" color="error" variant="outlined" />
                  )}

                  {tx.viesti && (
                    <Chip size="small" label={tx.viesti} variant="outlined" />
                  )}
                  {tx.cardId && <Chip size="small" label={`Kortti: ${tx.cardId}`} color="info" />}
                </Box>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Split Dialog */}
      <Dialog
        open={splitDialogOpen}
        onClose={() => setSplitDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Jaa tapahtuma
          {splitTx && (
            <Typography variant="body2" color="text.secondary">
              {splitTx.rawPayeeName} — {formatEur(splitTx.amountEur)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {splitError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {splitError}
            </Typography>
          )}
          {splitLines.map((line, idx) => (
            <Box key={idx} sx={{ mb: 2 }}>
              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  label="Summa"
                  type="number"
                  value={line.amountEur}
                  onChange={(e) => updateSplitLine(idx, 'amountEur', parseFloat(e.target.value) || 0)}
                  size="small"
                  sx={{ width: 120 }}
                />
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Kategoria</InputLabel>
                  <Select
                    value={line.categoryId}
                    label="Kategoria"
                    onChange={(e) => updateSplitLine(idx, 'categoryId', e.target.value)}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton
                  onClick={() => removeSplitLine(idx)}
                  disabled={splitLines.length <= 1}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              <TextField
                label="Muistiinpano"
                value={line.note}
                onChange={(e) => updateSplitLine(idx, 'note', e.target.value)}
                size="small"
                fullWidth
                sx={{ mt: 1 }}
              />
              {idx < splitLines.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))}
          <Button startIcon={<AddIcon />} onClick={addSplitLine} size="small">
            Lisää rivi
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Summa: {formatEur(splitLines.reduce((a, l) => a + Number(l.amountEur), 0))} /{' '}
            {formatEur(splitTx?.amountEur || 0)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSplitDialogOpen(false)}>Peruuta</Button>
          <Button variant="contained" onClick={handleSplitSave}>
            Tallenna jako
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
